'use client'

import { useMemo } from 'react'

import { CATALOG_FETCHABLE_EXCHANGE_LIST } from '@/features/dashboard/lib/supported-exchanges'
import {
  buildUnderlyingBacklinks,
  selectCrossExchangeOptionChain,
} from '@/lib/instruments/underlying-ref'
import { useMasterInstrumentLists } from '@/service/master-instrument.hook'
import { useEnabledExchangeList, useVenuesLoaded } from '@/service/venue.hook'
import { Exchange, Instrument, InstrumentKind } from '@/types/generated/common/types'

const EMPTY_INSTRUMENTS: Instrument[] = []

/**
 * The option chain that trades against `underlying` on a DIFFERENT exchange.
 *
 * A chart widget's instrument universe is a single `useInstrumentList(selectedTabExchange)`
 * call pinned to the tab's own exchange, which is why an XNAS IBIT tab shows no option
 * toggle: IBIT's options list on OPRA, so no search of the XNAS list can reach them.
 * This walks `Instrument.underlying` backwards over the full catalog to find them.
 *
 * The catalog comes from `useMasterInstrumentLists`, which the dashboard already
 * prefetches for every enabled venue on mount — so this shares those cache entries
 * rather than issuing fetches of its own.
 *
 * Returns an empty chain (and `exchange: undefined`) whenever the tab's own exchange
 * already carries the options, since the widget's existing single-exchange path
 * handles that case and merging the two would list the same chain twice.
 */
export function useCrossExchangeOptions(
  underlying: Instrument | null | undefined,
  enabled = true
): {
  instruments: Instrument[]
  exchange: Exchange | undefined
} {
  const catalogExchanges = useEnabledExchangeList(CATALOG_FETCHABLE_EXCHANGE_LIST)
  const venuesLoaded = useVenuesLoaded()
  const shouldResolve =
    enabled &&
    venuesLoaded &&
    underlying?.kind === InstrumentKind.INSTRUMENT_KIND_SPOT &&
    underlying.isActive
  const { instruments: fetchedCatalog } = useMasterInstrumentLists(catalogExchanges, shouldResolve)
  const catalog = shouldResolve ? fetchedCatalog : EMPTY_INSTRUMENTS

  const backlinks = useMemo(() => buildUnderlyingBacklinks(catalog), [catalog])

  return useMemo(() => {
    if (!shouldResolve) return { instruments: [], exchange: undefined }
    return selectCrossExchangeOptionChain(underlying, backlinks)
  }, [backlinks, shouldResolve, underlying])
}
